import React, { useState } from 'react';
import { CheckCircle2, CreditCard, Shield, Code2, Database, Layout, Smartphone, FileCode, Layers, BrainCircuit, HelpCircle, FileCheck2, Clock, Mail } from 'lucide-react';
import PaymentModal from '../ui/PaymentModal';
import API_URL from '../../config';

const programDeliverables = [
  '4-Week Hands-on Applied Project Curriculum',
  'Automated Code Evaluation & Sandbox Testing',
  'Constructive Project Review & Architectural Feedback',
  'Verifiable Digital Certificate (Upon Task Completion)',
  'Interview Technical Preparation & Guidance'
];

const technicalTracks = [
  { name: 'Python', icon: <Code2 className="w-4 h-4 text-brand-600" /> },
  { name: 'Java', icon: <Layers className="w-4 h-4 text-indigoBrand-600" /> },
  { name: 'DSA', icon: <BrainCircuit className="w-4 h-4 text-emerald-600" /> },
  { name: 'Cyber Security', icon: <Shield className="w-4 h-4 text-brand-600" /> },
  { name: 'Data Analytics', icon: <Database className="w-4 h-4 text-amber-600" /> },
  { name: '.NET Core', icon: <FileCode className="w-4 h-4 text-purple-600" /> },
  { name: 'Kotlin', icon: <Smartphone className="w-4 h-4 text-emerald-600" /> },
  { name: 'Web Tech', icon: <Layout className="w-4 h-4 text-indigoBrand-600" /> }
];

const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PaidInternship = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('Python');

  const handleDomainClick = (domainName) => {
    setSelectedDomain(domainName);
  };

  const displayRazorpay = async (userData) => {
    const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    if (!res) {
      alert('Payment gateway failed to load. Please check your network connection.');
      return;
    }

    try {
      const data = await fetch(`${API_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      }).then((t) => t.json());

      if (!data.orderId) {
        alert('Could not initiate order. Please try again later or contact support.');
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_dummy',
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'Mira Future Tech Vision Pvt Ltd',
        description: `Assessment Track: ${userData.domain}`,
        image: '/logo192.jpeg',
        order_id: data.orderId,
        handler: async function (response) {
          const verifyData = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          };

          const verifyRes = await fetch(`${API_URL}/api/payment/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(verifyData)
          }).then((t) => t.json());

          if (verifyRes.message) {
            alert('Enrollment successful! Check your email for track access details.');
            setIsModalOpen(false);
          } else {
            alert('Payment verification could not be completed.');
          }
        },
        prefill: {
          name: userData.name,
          email: userData.email,
          contact: userData.phone,
        },
        theme: {
          color: '#0284c7'
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error(error);
      alert('An error occurred during payment processing.');
    }
  };

  return (
    <section id="paid-internship" className="py-20 relative bg-white dark:bg-slate-950 border-t border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              Skill Evaluation Program
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
              Technical Assessment & Certification Track
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-base max-w-2xl mx-auto">
              A transparent, self-paced evaluation module designed to validate hands-on engineering competency across key technology stacks.
            </p>
          </div>

          {/* Track Selection */}
          <div className="mb-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 text-center mb-4">
              Select Your Specialization Track:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {technicalTracks.map((track) => {
                const isSelected = selectedDomain === track.name;
                return (
                  <button
                    key={track.name}
                    type="button"
                    onClick={() => handleDomainClick(track.name)}
                    className={`p-3.5 rounded-xl border flex items-center justify-center gap-2.5 transition-all text-sm font-semibold ${
                      isSelected
                        ? 'border-brand-600 bg-brand-50/80 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 text-slate-700 dark:text-slate-300 hover:border-slate-300 hover:bg-slate-100/60'
                    }`}
                  >
                    {track.icon}
                    <span>{track.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Details & Pricing Card */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Left: What You Receive & Conditions */}
            <div className="lg:col-span-7 bg-slate-50/70 dark:bg-slate-900/40 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-7 space-y-6">
              <div>
                <h3 className="text-lg font-bold font-display text-slate-900 dark:text-white mb-4">
                  Program Deliverables
                </h3>
                <ul className="space-y-3">
                  {programDeliverables.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                      <CheckCircle2 size={16} className="text-brand-600 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Transparency Notice */}
              <div className="pt-5 border-t border-slate-200 dark:border-slate-800 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Transparency & Conditions
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-500">
                  <div className="flex items-start gap-2">
                    <Clock size={14} className="shrink-0 mt-0.5 text-slate-400" />
                    <span>Duration: 4 Weeks (Self-paced project milestones)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileCheck2 size={14} className="shrink-0 mt-0.5 text-slate-400" />
                    <span>Certification: Awarded upon verified task submission</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <HelpCircle size={14} className="shrink-0 mt-0.5 text-slate-400" />
                    <span>Refunds: Full refund within 3 days if portal is inaccessible</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail size={14} className="shrink-0 mt-0.5 text-slate-400" />
                    <span>Support: contact@mirafuturetech.com</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Transparent Pricing & Enrollment */}
            <div className="lg:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-7 shadow-card">
              <div className="mb-6">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Assessment Evaluation Fee
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">₹99</span>
                  <span className="text-xs text-slate-500 font-medium">/ complete track (one-time)</span>
                </div>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                  The nominal fee covers cloud evaluation sandbox costs, automated grading pipeline, and cryptographic verification certificate processing.
                </p>
              </div>

              <div className="space-y-3 mb-6 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <CreditCard size={15} className="text-brand-600" />
                  <span>Secure 256-bit encrypted checkout via Razorpay</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={15} className="text-brand-600" />
                  <span>Immediate enrollment confirmation</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="w-full py-3.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm shadow-sm hover:shadow transition-all duration-200"
              >
                Enroll in {selectedDomain} Track (₹99)
              </button>

              <p className="text-[11px] text-slate-400 text-center mt-4">
                Selected track: <span className="font-semibold text-slate-700 dark:text-slate-300">{selectedDomain}</span>. You can change tracks before payment.
              </p>
            </div>

          </div>

        </div>
      </div>

      <PaymentModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        selectedDomain={selectedDomain}
        onSubmit={displayRazorpay} 
      />
    </section>
  );
};

export default PaidInternship;
